import React, { useState, useEffect } from 'react';
import LoginButton from './LoginButton';
import { useToasts } from './TactileToasts';

interface MultiProviderLoginProps {
    className?: string;
}

const MultiProviderLogin: React.FC<MultiProviderLoginProps> = ({ className = "" }) => {
    const { showToast } = useToasts();
    const [authErrorMsg, setAuthErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const failedProvider = params.get('auth_error');
        const oauthError = params.get('error');

        if (failedProvider || oauthError) {
            localStorage.removeItem('synclothe_auth_pending');
            let msg = "Authentication could not be completed. Please select the provider matching your email account.";
            if (failedProvider === 'google') {
                msg = "Google sign-in failed or was cancelled. Please use a valid @gmail.com / Google account, or choose 'Authorize via Microsoft' for @hotmail.com / @outlook.com emails.";
            } else if (failedProvider === 'microsoft' || failedProvider === 'aad') {
                msg = "Microsoft sign-in failed or was cancelled. Please use a valid @hotmail.com / @outlook.com / Microsoft account, or choose 'Authorize via Google' for @gmail.com emails.";
            } else if (failedProvider === 'github') {
                msg = "GitHub authorization was cancelled or could not be completed. Please try again.";
            }
            setAuthErrorMsg(msg);
            showToast(msg, "error");

            // Clean up error query params from address bar without reloading
            params.delete('auth_error');
            params.delete('error');
            params.delete('error_description');
            const cleanSearch = params.toString();
            const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    }, [showToast]);

    return (
        <div className={`space-y-3 w-full max-w-sm mx-auto ${className}`}>
            {authErrorMsg && (
                <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-left space-y-1 animate-fade-in">
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-400">
                        ⚠️ Authentication Notice
                    </p>
                    <p className="text-[11px] text-gray-300 leading-relaxed">
                        {authErrorMsg}
                    </p>
                </div>
            )}
            <LoginButton 
                provider="github" 
                className="w-full btn-azure py-3 px-6 text-xs font-black uppercase tracking-[0.2em] shadow-lg flex items-center justify-center gap-3"
                text="Authorize via GitHub" 
            />
            <LoginButton 
                provider="microsoft" 
                className="w-full bg-azure-900/40 hover:bg-azure-800/60 text-azure-300 border border-azure-500/30 py-3 px-6 text-xs font-black uppercase tracking-[0.2em] rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                text="Authorize via Microsoft (Hotmail / Outlook)" 
            />
            <LoginButton 
                provider="google" 
                className="w-full bg-white/10 hover:bg-white/15 text-white border border-white/20 py-3 px-6 text-xs font-black uppercase tracking-[0.2em] rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                text="Authorize via Google (Gmail)" 
            />
        </div>
    );
};

export default MultiProviderLogin;
